import React, { useState } from "react";
import Seo from "../components/Seo";
import { STATIC_PAGES } from "../seo/openGraph";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { CaseStudiesHeroVisual } from "../components/HeroVisuals";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" },
  }),
};

const categories = ["All", "AI", "Software Engineering", "MVP Development", "Creative Design", "Data & Analytics", "Remote Teams"];

const caseStudies = [
  { name: "zLinq", categories: ["Creative Design"], desc: "Ongoing UX/UI for zLinq's telecom lifecycle dashboard (assets, savings, feed, and navigation) designed in Figma with the CTO so the product team can ship it.", url: "https://www.maxiomtech.com/case-study/zlinq/" },
  { name: "AT&T", categories: ["Creative Design", "Software Engineering"], desc: "Enterprise mobile and web work for AT&T Government Solutions.", url: "https://www.maxiomtech.com/case-study/att/" },
  { name: "Microsoft", categories: ["MVP Development"], desc: "MVP prototyping and deployment for Microsoft teams.", url: "https://www.maxiomtech.com/case-study/microsoft/" },
  { name: "BMW Performance Center", categories: ["Creative Design", "Remote Teams", "Software Engineering"], desc: "Digital experience for BMW's performance driving center.", url: "https://www.maxiomtech.com/case-study/bmw-performance-center/" },
  { name: "Toastmasters International", categories: ["AI", "Software Engineering"], desc: "Platform work for the global public-speaking organization.", url: "https://www.maxiomtech.com/case-study/toastmasters-international/" },
  { name: "BrokerBlocks", categories: ["AI", "Remote Teams"], desc: "AI-driven real estate technology platform streamlining broker operations and deal management.", url: "https://www.maxiomtech.com/case-study/brokerblocks/" },
  { name: "SmileML", categories: ["AI", "Remote Teams"], desc: "Machine learning platform for dental diagnostics, improving patient outcomes through AI-powered analysis.", url: "https://www.maxiomtech.com/case-study/smileml/" },
  { name: "PAN Foundation", categories: ["Software Engineering"], desc: "Custom software platform supporting patient access to medications through streamlined assistance programs.", url: "https://www.maxiomtech.com/case-study/pan-foundation/" },
  { name: "Hyperlogs", categories: ["MVP Development", "Software Engineering"], desc: "Full-stack MVP for a logging and monitoring SaaS platform.", url: "https://www.maxiomtech.com/case-study/hyperlogs/" },
  { name: "FiscalNote", categories: ["Software Engineering"], desc: "Enterprise software engineering for the leading government relations and regulatory tracking platform.", url: "https://www.maxiomtech.com/case-study/fiscalnote/" },
  { name: "CrowdKeep", categories: ["AI", "MVP Development"], desc: "AI-powered IoT platform for workplace safety, enabling real-time proximity tracking and contact tracing.", url: "https://www.maxiomtech.com/case-study/crowdkeep/" },
  { name: "Yes! Communities", categories: ["Data & Analytics", "Software Engineering"], desc: "Data analytics and software solutions for one of the largest manufactured home communities in the US.", url: "https://www.maxiomtech.com/case-study/yes-communities/" },
  { name: "Online Rewards", categories: ["Remote Teams", "Software Engineering"], desc: "Scalable rewards platform development with remote engineering teams for B2B loyalty solutions.", url: "https://www.maxiomtech.com/case-study/online-rewards/" },
  { name: "Open Tech Alliance", categories: ["Creative Design", "Software Engineering"], desc: "Design and engineering for self-storage technology solutions, modernizing the industry's digital presence.", url: "https://www.maxiomtech.com/case-study/open-tech-alliance/" },
  { name: "SCS Engineers", categories: ["AI", "Creative Design"], desc: "AI-enhanced environmental consulting platform with modern creative design for improved user experience.", url: "https://www.maxiomtech.com/case-study/scs-engineers/" },
  { name: "TruthQuotient", categories: ["AI", "MVP Development"], desc: "AI-powered media intelligence platform built from MVP to production, analyzing news credibility at scale.", url: "https://www.maxiomtech.com/case-study/truthquotient/" },
  { name: "American Water Works Association", categories: ["MVP Development", "Software Engineering"], desc: "Digital transformation for the largest water industry organization, modernizing member services.", url: "https://www.maxiomtech.com/case-study/american-water-works-association/" },
];

export default function CaseStudiesPage() {
  const [activeCategory, setActiveCategory] = useState("All");

  const filtered = activeCategory === "All"
    ? caseStudies
    : caseStudies.filter((cs) => cs.categories.includes(activeCategory));

  return (
    <>
      <Seo {...STATIC_PAGES.caseStudies} />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeUp} initial="hidden" animate="visible">
              <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">Our Work</p>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6" data-testid="case-studies-heading">
                Case Studies.
              </h1>
              <p className="text-lg text-neutral-300 max-w-xl">
                Named case studies from AT&T, Microsoft, BMW, Toastmasters, and more. Write-ups live on maxiomtech.com. We don&apos;t invent metrics here.
              </p>
            </motion.div>
            <CaseStudiesHeroVisual />
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="pb-8 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex flex-wrap gap-2" data-testid="case-studies-filter">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  activeCategory === cat
                    ? "bg-[#00AEEF] text-white"
                    : "bg-[#121212] text-gray-400 hover:text-white border border-white/5 hover:border-white/20"
                }`}
                data-testid={`filter-${cat.toLowerCase().replace(/[& ]/g, "-")}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-20 md:pb-32 bg-[#0A0A0A]" data-testid="case-studies-grid">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filtered.map((cs, i) => (
              <motion.a
                key={cs.name}
                href={cs.url}
                target="_blank"
                rel="noopener noreferrer"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.05}
                className="block bg-[#121212] border border-white/5 rounded-xl p-8 hover:border-[#00AEEF]/30 transition-all duration-300 group"
                data-testid={`case-study-item-${i}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex flex-wrap gap-2">
                    {cs.categories.map((c) => (
                      <span key={c} className="text-xs text-[#00AEEF] bg-[#00AEEF]/10 px-2 py-1 rounded">{c}</span>
                    ))}
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-[#00AEEF] transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#00AEEF] transition-colors">{cs.name}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{cs.desc}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#0f0f0f]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              Ready to Be Our Next Success Story?
            </h2>
            <p className="text-gray-400 text-lg mb-10">
              Let's discuss how Maxiom Apps can help you achieve your technology goals.
            </p>
            <Link to="/contact" data-testid="case-studies-cta">
              <Button className="bg-[#00AEEF] hover:bg-[#0095CC] text-white font-medium px-8 py-6 text-base rounded-md transition-all duration-300 shadow-[0_0_20px_rgba(0,174,239,0.3)] hover:shadow-[0_0_30px_rgba(0,174,239,0.5)]">
                Schedule a Call <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
